import { Box, Button, Checkbox, CheckboxGroup, Flex, Input, Radio, RadioGroup, VisuallyHidden } from '@chakra-ui/react';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ChangeEvent, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_SELECTION_FIELD_OPTION_TEXT,
  LIMIT_SELECTION_FIELD_OPTIONS,
} from 'widgets/FormWidget/FormWidget.config';
import { FormWidgetData } from 'widgets/FormWidget/FormWidget.types';
import { useWidget } from 'widgets/sdk';

export const SelectionFieldOptions = () => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'formToolbar' });
  const { updateWidget, selectionFieldData } = useWidget<FormWidgetData>();

  const enableMultipleSelection = selectionFieldData?.enableMultipleSelection;
  const enableOther = selectionFieldData?.enableOther;
  const optionValueList = useMemo(() => selectionFieldData?.options?.map((x) => x.value) ?? [], [selectionFieldData]);
  const disableAddButton = useMemo(() => optionValueList.length >= LIMIT_SELECTION_FIELD_OPTIONS, [optionValueList]);

  // Delete the item of options
  const handleDeleteChoice = useCallback(
    (index: number) => {
      const newOptions = selectionFieldData?.options
        .filter((option) => option.index !== index)
        .map((option, i) => {
          return {
            ...option,
            index: i,
          };
        });

      updateWidget({
        selectionFieldData: {
          options: newOptions,
        },
      });
    },
    [updateWidget, selectionFieldData],
  );

  // Add the new item of options
  const handleAddChoice = useCallback(() => {
    const newOptions = [...(selectionFieldData?.options || [])];
    newOptions.push({ index: newOptions.length, value: DEFAULT_SELECTION_FIELD_OPTION_TEXT });

    updateWidget({
      selectionFieldData: {
        options: newOptions,
      },
    });
  }, [updateWidget, selectionFieldData]);

  const handleAddOther = useCallback(() => {
    if (enableOther) return;

    updateWidget({
      selectionFieldData: {
        enableOther: true,
      },
    });
  }, [updateWidget, enableOther]);

  const handleDeleteOther = useCallback(() => {
    if (!enableOther) return;

    updateWidget({
      selectionFieldData: {
        enableOther: false,
      },
    });
  }, [updateWidget, enableOther]);

  return (
    <Box w='full'>
      {enableMultipleSelection ? (
        <CheckboxGroup value={['']} isDisabled>
          <Flex gap={2} flexDir={'column'}>
            {optionValueList.map((value, index) => (
              <Flex key={index} justifyContent={'flex-start'} w={'full'} p={0} m={0} alignItems={'center'}>
                <Checkbox variant={'selection-field-setting'} value={index.toString()}>
                  <VisuallyHidden _hover={{ background: 'white', color: 'red' }} backgroundColor='blue'>
                    {value}
                  </VisuallyHidden>
                </Checkbox>
                <ValueInput index={index} value={selectionFieldData?.options[index].value ?? ''} />
                <Button
                  variant='unstyled'
                  minW={5}
                  width={5}
                  height={5}
                  ml={2}
                  onClick={() => handleDeleteChoice(index)}
                >
                  <DeleteIcon width={20} height={20} color='var(--vg-colors-font-500)' />
                </Button>
              </Flex>
            ))}
            {enableOther && (
              <Flex justifyContent={'flex-start'} w={'full'} p={0} m={0} alignItems={'center'}>
                <Checkbox variant={'selection-field-setting'} value={optionValueList.length.toString()}>
                  <VisuallyHidden>{t('settingsSideBar.question.addOtherPlaceholder')}</VisuallyHidden>
                </Checkbox>
                <Input
                  isDisabled={true}
                  size={'sm'}
                  borderRadius='base'
                  placeholder={t('settingsSideBar.question.addOtherPlaceholder')}
                />
                <Button variant='unstyled' minW={5} width={5} height={5} ml={2} onClick={handleDeleteOther}>
                  <DeleteIcon width={20} height={20} color='var(--vg-colors-font-500)' />
                </Button>
              </Flex>
            )}
          </Flex>
        </CheckboxGroup>
      ) : (
        <RadioGroup value={''} isDisabled>
          <Flex gap={2} flexDir={'column'}>
            {optionValueList.map((value, index) => (
              <Flex key={index} justifyContent={'flex-start'} w={'full'} p={0} m={0} alignItems={'center'}>
                <Radio variant={'selection-field-setting'} value={index.toString()}>
                  <VisuallyHidden>{value}</VisuallyHidden>
                </Radio>
                <ValueInput index={index} value={selectionFieldData?.options[index].value ?? ''} />
                <Button
                  variant='unstyled'
                  minW={5}
                  width={5}
                  height={5}
                  ml={2}
                  onClick={() => handleDeleteChoice(index)}
                >
                  <DeleteIcon width={20} height={20} color='var(--vg-colors-font-500)' />
                </Button>
              </Flex>
            ))}
            {enableOther && (
              <Flex justifyContent={'flex-start'} w={'full'} p={0} m={0} alignItems={'center'}>
                <Radio variant={'selection-field-setting'} value={optionValueList.length.toString()}>
                  <VisuallyHidden>{t('settingsSideBar.question.addOtherPlaceholder')}</VisuallyHidden>
                </Radio>
                <Input
                  isDisabled={true}
                  size={'sm'}
                  borderRadius='base'
                  placeholder={t('settingsSideBar.question.addOtherPlaceholder')}
                />
                <Button variant='unstyled' minW={5} width={5} height={5} ml={2} onClick={handleDeleteOther}>
                  <DeleteIcon width={20} height={20} color='var(--vg-colors-font-500)' />
                </Button>
              </Flex>
            )}
          </Flex>
        </RadioGroup>
      )}
      <Flex gap='2'>
        {!enableOther && (
          <Button
            variant='outline'
            isDisabled={disableAddButton}
            borderRadius={'base'}
            w='full'
            mt={4}
            size='sm'
            fontSize='sm'
            fontWeight='medium'
            onClick={handleAddOther}
          >
            {t('settingsSideBar.question.addOther')}
          </Button>
        )}
        <Button
          variant='action'
          isDisabled={disableAddButton}
          borderRadius={'base'}
          w='full'
          mt={4}
          size='sm'
          fontSize='sm'
          fontWeight='medium'
          onClick={handleAddChoice}
        >
          {t('settingsSideBar.question.addChoice')}
        </Button>
      </Flex>
    </Box>
  );
};

SelectionFieldOptions.displayName = 'Selection Field Options';
export default SelectionFieldOptions;

type ValueInputProps = {
  index: number;
  value: string;
};

const ValueInput = memo(({ index, value }: ValueInputProps) => {
  const { updateWidget, selectionFieldData } = useWidget<FormWidgetData>();
  const [localLabel, setLocalLabel] = useState(value);

  const updateValue = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setLocalLabel(newValue);

      // Update only options of index
      let newOptions = selectionFieldData?.options.map((option) =>
        option.index === index
          ? {
              ...option,
              value: newValue,
            }
          : option,
      );
      updateWidget({
        selectionFieldData: {
          options: newOptions,
        },
      });
    },
    [updateWidget, index, selectionFieldData],
  );
  return <Input value={localLabel} onChange={updateValue} size={'sm'} borderRadius='base' />;
});
ValueInput.displayName = 'Value Input';
